import { ApplicationModuleCode } from '@/domain/application-modules/application-module.interface';
import { ApplicationModulesService } from '@/domain/application-modules/application-modules.service';
import { BusinessIntelligenceDataSourceCode, type BusinessIntelligenceFilter } from '@/interfaces/BusinessIntelligence.interface';
import type { ClassificationNodeId } from '@/interfaces/Classification.interface';
import { BusinessIntelligenceProvider } from '@/providers/business-intelligence/business-intelligence.provider';
import { ProfileProvider } from '@/providers/profile/profile.provider';
import { DATABOARD_PAGE } from '@/providers/rights.constants';
import { Page, RoutingService } from '@/services/routing/routing.service';
import { inject, Injectable } from '@angular/core';
import type { CanActivateChildFn, CanActivateFn } from '@angular/router';
import { combineLatest, map, type Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class CustomBusinessIntelligenceAnalysisFeatureService {
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _routingService = inject(RoutingService);
    private _profileProvider = inject(ProfileProvider);
    private _applicationModulesProvider = inject(ApplicationModulesService);

    async goToFeature(params: {
        classificationNodeId?: ClassificationNodeId;
        filters?: { dataSource: BusinessIntelligenceDataSourceCode; value: BusinessIntelligenceFilter[] };
    }) {
        this._routingService.goTo(Page.CUSTOM_BUSINESS_INTELLIGENCE_ANALYSIS, {
            classificationNodeId: params.classificationNodeId,
            filters: params.filters
                ? await this._businessIntelligenceProvider.convertFilters(
                      params.filters.dataSource,
                      BusinessIntelligenceDataSourceCode.CONSUMPTION,
                      params.filters.value
                  )
                : []
        });
    }

    hasFeatureAccess$(): Observable<boolean> {
        return combineLatest([
            this._profileProvider.hasRight$(DATABOARD_PAGE),
            this._applicationModulesProvider.hasModuleAccess$(ApplicationModuleCode.DATABOARDS)
        ]).pipe(map(([hasRight, hasModule]) => hasRight && hasModule));
    }

    async hasFeatureAccess(): Promise<boolean> {
        const hasRight = await this._profileProvider.hasRight(DATABOARD_PAGE);
        const hasModuleAccess = await this._profileProvider.hasRight(DATABOARD_PAGE);
        return hasRight && hasModuleAccess;
    }
}

export const customBusinessIntelligenceAnalysisGuard: CanActivateFn & CanActivateChildFn = async () => {
    const featureService = inject(CustomBusinessIntelligenceAnalysisFeatureService);
    const hasFeatureAccess = await featureService.hasFeatureAccess();

    if (!hasFeatureAccess) {
        // redirect ?
        return false;
    }
    return true;
};
